import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Home,
  Briefcase,
  Sparkles,
  User,
  BookOpen,
  MapPin,
  ChevronRight,
  ChevronDown,
  Building2,
  Bell,
  Award,
  Zap,
  Flame,
  Search,
  Trophy,
  GraduationCap,
  Globe,
  Share2,
  Rocket,
  FileText,
  ExternalLink,
  Menu
} from "lucide-react";
import axios from "axios";
import { useAuth, API } from "@/App";
import BottomNavNew from "@/components/BottomNavNew";

const COURSE_CATEGORIES = [
  { id: 1, name: "Business Operations", icon: "💼" },
  { id: 2, name: "Cloud Computing", icon: "☁️" },
  { id: 3, name: "Communication", icon: "💬" },
  { id: 4, name: "Continuing Education Units", icon: "🎓" },
  { id: 5, name: "Cybersecurity", icon: "🔒" },
  { id: 6, name: "Data Science", icon: "📊" },
  { id: 7, name: "Design Tools", icon: "🎨" },
  { id: 8, name: "Development", icon: "💻" },
  { id: 9, name: "Finance & Accounting", icon: "💰" },
  { id: 10, name: "Generative AI", icon: "🤖" },
  { id: 11, name: "IT Operations", icon: "⚙️" },
  { id: 12, name: "Language Learning", icon: "🌍" },
  { id: 13, name: "Leadership & Management", icon: "👔" },
  { id: 14, name: "Marketing", icon: "📢" },
  { id: 15, name: "Personal Development", icon: "🌱" },
  { id: 16, name: "Productivity", icon: "⚡" },
  { id: 17, name: "Project & Product Management", icon: "📋" },
  { id: 18, name: "Sales & Customer Service", icon: "🤝" },
  { id: 19, name: "Workplace & Human Resources", icon: "👥" },
  { id: 20, name: "UX, Web & Mobile Design", icon: "📱" }
];

const QUICK_LINKS = [
  { id: 1, name: "JunicornFinder", icon: Search, color: "violet", url: "https://dev.junicornshub.com", external: true },
  { id: 2, name: "TalentON Hackathon", icon: Trophy, color: "amber", url: "https://explore.talenton.ai/", external: true },
  { id: 3, name: "Talent Pipeline Program", icon: GraduationCap, color: "emerald", url: "/karnataka-model", external: false },
  { id: 4, name: "Gemma Offline AI", icon: Globe, color: "green", url: "/gemma", external: false },
  { id: 5, name: "Content Command Centre", icon: Sparkles, color: "fuchsia", url: "/content", external: false },
  { id: 6, name: "Captain Command Centre", icon: Award, color: "blue", url: "/command-centre", external: false },
  { id: 7, name: "WhatsApp NDA Sign", icon: Share2, color: "green", url: "/whatsapp", external: false }
];

export default function DoerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [jobsRes] = await Promise.all([
        axios.get(`${API}/jobs`, { params: { division: user?.division, limit: 5 } })
      ]);
      setJobs(jobsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const doersId = `RDW-${user?.pincode || '000000'}-${(user?.division || 'GEN').slice(0, 3).toUpperCase()}-L1`;
  const userStreak = 7;

  const handleQuickLinkClick = (link) => {
    if (link.external) {
      window.open(link.url, '_blank');
    } else {
      navigate(link.url);
    }
    setActiveDropdown(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white sticky top-0 z-50 shadow-lg">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-white/70 text-xs">Welcome back,</p>
            <h1 className="font-display text-lg font-bold">{user?.name || "Doer"}</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full">
              <Flame className="w-3 h-3 text-orange-300" />
              <span className="font-bold text-xs">{userStreak}</span>
            </div>
            <button className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Bell className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Navigation Bar */}
        <nav className="bg-white/10 backdrop-blur-sm border-t border-white/10">
          <div className="px-4 py-2 flex items-center gap-6 overflow-x-auto">
            {/* Quick Links Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('quicklinks')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1 text-white/90 hover:text-white text-sm font-medium py-2 whitespace-nowrap">
                <Zap className="w-4 h-4" />
                Quick Links
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {activeDropdown === 'quicklinks' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 min-w-[240px] overflow-hidden"
                  >
                    {QUICK_LINKS.map((link) => {
                      const Icon = link.icon;
                      return (
                        <button
                          key={link.id}
                          onClick={() => handleQuickLinkClick(link)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left border-b last:border-b-0 border-slate-100"
                        >
                          <Icon className={`w-4 h-4 text-${link.color}-600`} />
                          <span className="text-slate-900 text-sm font-medium flex-1">{link.name}</span>
                          {link.external && <ExternalLink className="w-3 h-3 text-slate-400" />}
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* My Reports Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('reports')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1 text-white/90 hover:text-white text-sm font-medium py-2 whitespace-nowrap">
                <FileText className="w-4 h-4" />
                Suitability Report
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {activeDropdown === 'reports' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 min-w-[240px] p-4"
                  >
                    <p className="text-slate-500 text-sm text-center mb-3">No reports yet</p>
                    <Button
                      size="sm"
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => {
                        navigate('/aimee-analyzer');
                        setActiveDropdown(null);
                      }}
                    >
                      <Sparkles className="w-3 h-3 mr-2" />
                      Take Assessment
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Courses Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('courses')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1 text-white/90 hover:text-white text-sm font-medium py-2 whitespace-nowrap">
                <BookOpen className="w-4 h-4" />
                Talenton Program
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {activeDropdown === 'courses' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 w-[320px] max-h-[400px] overflow-y-auto"
                  >
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {COURSE_CATEGORIES.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => {
                            console.log(`Course: ${category.name}`);
                            setActiveDropdown(null);
                          }}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded text-left"
                        >
                          <span className="text-lg">{category.icon}</span>
                          <span className="text-slate-900 text-xs font-medium">{category.name}</span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Jobs Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('jobs')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1 text-white/90 hover:text-white text-sm font-medium py-2 whitespace-nowrap">
                <Briefcase className="w-4 h-4" />
                Jobs4Me
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {activeDropdown === 'jobs' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 min-w-[280px] max-h-[400px] overflow-y-auto"
                  >
                    {loading ? (
                      <div className="px-4 py-3">
                        <p className="text-slate-500 text-sm">Loading jobs...</p>
                      </div>
                    ) : jobs.length > 0 ? (
                      <>
                        {jobs.map((job) => (
                          <button
                            key={job.id}
                            onClick={() => {
                              navigate(`/jobs?selected=${job.id}`);
                              setActiveDropdown(null);
                            }}
                            className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left border-b last:border-b-0 border-slate-100"
                          >
                            <Building2 className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-slate-900 text-sm font-medium truncate">{job.title}</p>
                              <p className="text-slate-500 text-xs truncate">{job.company_name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className="bg-purple-100 text-purple-700 text-[10px]">{job.level}</Badge>
                              </div>
                            </div>
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            navigate('/jobs');
                            setActiveDropdown(null);
                          }}
                          className="w-full px-4 py-2 text-purple-600 hover:bg-purple-50 text-sm font-medium border-t border-slate-100"
                        >
                          View All Jobs →
                        </button>
                      </>
                    ) : (
                      <div className="px-4 py-3">
                        <p className="text-slate-500 text-sm">No jobs available</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* User Profile Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('profile')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1 text-white/90 hover:text-white text-sm font-medium py-2 whitespace-nowrap">
                <User className="w-4 h-4" />
                Doers Profile
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {activeDropdown === 'profile' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 min-w-[240px] overflow-hidden"
                  >
                    <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                          {user?.name?.charAt(0) || "D"}
                        </div>
                        <div>
                          <p className="text-slate-900 font-semibold text-sm">{user?.name || "Doer"}</p>
                          <p className="text-slate-500 text-xs font-mono">{doersId}</p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setActiveDropdown(null);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
                    >
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-900 text-sm">View Profile</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/dp');
                        setActiveDropdown(null);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
                    >
                      <Award className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-900 text-sm">Doers Profile (DP)</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content - Simplified */}
      <main className="px-4 py-6 pb-24 max-w-6xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Welcome to Your Dashboard</h2>
          <p className="text-slate-600 mb-6">Use the navigation menu above to access Quick Links, Reports, Courses, Jobs, and your Profile</p>

          {/* Quick CTA */}
          <Button
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90"
            onClick={() => navigate('/aimee-analyzer')}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Start Career Assessment
          </Button>
        </div>
      </main>

      {/* Bottom Navigation */}
      <BottomNavNew active="home" />
    </div>
  );
}
